import { useEffect, useRef, useState } from 'react'
import { Accordion as AccordionPrimitive } from 'radix-ui'
import { HugeiconsIcon } from '@hugeicons/react'
import { MinusSignIcon, Add01Icon } from '@hugeicons/core-free-icons'
import { useQuery } from 'convex/react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'

import { usePositionsAccordionStore } from '../stores'
import { PositionCard } from './PositionCard'
import type { DetailedPosition, GroupedPosition } from '../types'
import { EmptyState } from '@/components/common/empty-state'
import { Accordion, AccordionItem } from '@/components/ui/accordion'
import {
  PretextedAccordionContent,
  PretextedAccordionItem,
} from '@/components/common/pretexted-accordion'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

interface LevelCardProps {
  levelGroup: GroupedPosition['levels'][0]
  roleGroup: GroupedPosition
  isLevelOpen: boolean
  onViewQuestions: (
    positionId: string,
    title: string,
    level: string,
    role: string,
  ) => void
  initialPositionId?: string
  isInitialLevel?: boolean
}

const LevelCard = ({
  levelGroup,
  roleGroup,
  isLevelOpen,
  onViewQuestions,
  initialPositionId,
  isInitialLevel,
}: LevelCardProps) => {
  const position = levelGroup.positions[0]!

  // Eagerly fetch position details (even when collapsed) so content is ready on open
  const detailData = useQuery(api.positions.getPositionDetails, {
    positionId: position.id as Id<'positions'>,
  })

  // Scroll to this level row when navigating via ?role=&level= deep-link
  const itemRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!isInitialLevel || !isLevelOpen) return
    const t = setTimeout(() => {
      itemRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 200) // accordion animation is 180ms
    return () => clearTimeout(t)
  }, [isInitialLevel, isLevelOpen])

  // Highlight + scroll on mount when navigating directly to a position
  const wrapperRef = useRef<HTMLDivElement>(null)
  const highlightOnMount = initialPositionId === position.id
  useEffect(() => {
    if (!highlightOnMount || !isLevelOpen || !wrapperRef.current) return
    try {
      wrapperRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      wrapperRef.current.classList.add('ring', 'ring-primary', 'ring-2')
      const t = setTimeout(() => {
        wrapperRef.current?.classList.remove('ring', 'ring-primary', 'ring-2')
      }, 2000)
      return () => clearTimeout(t)
    } catch {
      // ignore
    }
  }, [highlightOnMount, isLevelOpen])

  return (
    <PretextedAccordionItem
      ref={itemRef}
      value={levelGroup.level}
      className="border-b border-border/40 last:border-b-0"
    >
      <div className="flex items-center justify-between py-2 gap-x-3">
        <span className="font-semibold text-lg shrink-0">
          {levelGroup.level}
        </span>
        <div className="flex gap-2 shrink-0">
          <AccordionPrimitive.Trigger asChild>
            <Button variant="default" size="sm">
              {isLevelOpen ? 'Hide requirements' : 'See requirements'}
            </Button>
          </AccordionPrimitive.Trigger>
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onViewQuestions(
                position.id,
                position.title,
                position.level,
                roleGroup.role,
              )
            }}
          >
            See questions
          </Button>
        </div>
      </div>

      <PretextedAccordionContent innerClassName="pt-3">
        <div ref={wrapperRef}>
          {detailData === undefined ? (
            <div className="w-full animate-pulse">
              <div className="h-4 bg-muted rounded mb-2" />
              <div className="h-3 bg-muted rounded mb-1" />
              <div className="h-3 bg-muted rounded" />
            </div>
          ) : (
            <PositionCard
              position={(detailData as DetailedPosition | null) ?? position}
            />
          )}
        </div>
      </PretextedAccordionContent>
    </PretextedAccordionItem>
  )
}

interface PositionsAccordionProps {
  groupedPositions: Array<GroupedPosition>
  onViewQuestions: (
    positionId: string,
    positionTitle: string,
    positionLevel: string,
    positionRole: string,
  ) => void
  className?: string
  initialOpenRole?: string
  initialOpenLevel?: string
  initialPositionId?: string
}

export const PositionsAccordion = ({
  groupedPositions,
  onViewQuestions,
  className,
  initialOpenRole,
  initialOpenLevel,
  initialPositionId,
}: PositionsAccordionProps) => {
  // Get accordion state from Zustand store
  const {
    openRoles: storedRoles,
    openLevels: storedLevels,
    setAccordionState,
  } = usePositionsAccordionStore()

  // Use URL params if provided, otherwise fall back to store state
  const initialRoles = initialOpenRole ? [initialOpenRole] : storedRoles
  const initialLevels =
    initialOpenRole && initialOpenLevel
      ? [`${initialOpenRole}-${initialOpenLevel}`]
      : storedLevels

  const [openRoles, setOpenRoles] = useState<Array<string>>(initialRoles)
  const [openLevels, setOpenLevels] = useState<Array<string>>(initialLevels)

  // Refs for each role card — used to scroll the target role into view when
  // navigating via ?role= deep-link (e.g. /positions/?role=Engineering&level=IC4)
  const roleCardRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  // Scroll to the target role card when only a role is specified (no level)
  // When a level is also specified, LevelCard handles scrolling to that row instead
  useEffect(() => {
    if (!initialOpenRole || initialOpenLevel) return
    const t = setTimeout(() => {
      roleCardRefs.current.get(initialOpenRole)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }, 200) // accordion animation is 180ms
    return () => clearTimeout(t)
  }, [initialOpenRole, initialOpenLevel])

  if (groupedPositions.length === 0) {
    return <EmptyState title="No positions found" />
  }

  const handleRoleValueChange = (value: Array<string>) => {
    setOpenRoles(value)
    setAccordionState(value, openLevels)
  }

  const handleLevelValueChange =
    (rolePrefix: string) => (value: Array<string>) => {
      // Prefix level values with role to make them unique
      const prefixedValues = value.map((v) => `${rolePrefix}-${v}`)
      const newLevels = [
        ...openLevels.filter((v) => !v.startsWith(`${rolePrefix}-`)),
        ...prefixedValues,
      ]
      setOpenLevels(newLevels)
      setAccordionState(openRoles, newLevels)
    }

  return (
    <div className={className}>
      <Accordion
        type="multiple"
        className="space-y-4"
        value={openRoles}
        onValueChange={handleRoleValueChange}
      >
        {groupedPositions.map((roleGroup) => {
          const isRoleOpen = openRoles.includes(roleGroup.role)

          return (
            <div
              key={roleGroup.role}
              ref={(el) => {
                if (el) roleCardRefs.current.set(roleGroup.role, el)
                else roleCardRefs.current.delete(roleGroup.role)
              }}
            >
              <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
                <AccordionItem value={roleGroup.role} className="border-none">
                  <AccordionPrimitive.Header className="flex">
                    <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between py-0 outline-none">
                      <span className="font-semibold text-lg">
                        {roleGroup.role}
                      </span>
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg border-2 border-muted/80 bg-muted/80 hover:bg-muted transition-colors">
                        {isRoleOpen ? (
                          <HugeiconsIcon
                            icon={MinusSignIcon}
                            className="text-muted-foreground size-5 shrink-0"
                          />
                        ) : (
                          <HugeiconsIcon
                            icon={Add01Icon}
                            className="text-muted-foreground size-5 shrink-0"
                          />
                        )}
                      </div>
                    </AccordionPrimitive.Trigger>
                  </AccordionPrimitive.Header>
                  <PretextedAccordionContent innerClassName="pb-0">
                    <Accordion
                      type="multiple"
                      className="mt-2"
                      value={openLevels
                        .filter((v) => v.startsWith(`${roleGroup.role}-`))
                        .map((v) => v.replace(`${roleGroup.role}-`, ''))}
                      onValueChange={handleLevelValueChange(roleGroup.role)}
                    >
                      {roleGroup.levels.map((levelGroup) => {
                        const isLevelOpen = openLevels.includes(
                          `${roleGroup.role}-${levelGroup.level}`,
                        )
                        return (
                          <LevelCard
                            key={`${roleGroup.role}-${levelGroup.level}`}
                            levelGroup={levelGroup}
                            roleGroup={roleGroup}
                            isLevelOpen={isLevelOpen}
                            onViewQuestions={onViewQuestions}
                            initialPositionId={initialPositionId}
                            isInitialLevel={
                              roleGroup.role === initialOpenRole &&
                              levelGroup.level === initialOpenLevel
                            }
                          />
                        )
                      })}
                    </Accordion>
                  </PretextedAccordionContent>
                </AccordionItem>
              </Card>
            </div>
          )
        })}
      </Accordion>
    </div>
  )
}
